import {
  Fragment,
  type ComponentProps,
  type ComponentType,
  type ReactNode,
  type SyntheticEvent,
  useState,
} from "react";
import {
  Card as CardBase,
  Row as RowBase,
  Col as ColBase,
} from "react-bootstrap";
import {
  Tabs,
  Tab,
  Typography,
  Grid as GridBase,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Alert } from "@mui/material";
import ChannelListBase from "../ChannelList";
import { chunkArray, getFormatDate, getImage } from "@/helpers/utils";
import ContractDetailsCardBase from "./ContractDetailsCard";
import BrandLogoCardBase from "./BrandLogoCard";

type BrandProfileValue =
  | null
  | undefined
  | string
  | number
  | boolean
  | BrandChannel[]
  | unknown[];

type BrandProfileItem = {
  fileName?: string;
  key: string;
  label: string;
  value: BrandProfileValue;
};

type BrandChannel = {
  channel_name: string;
  id: string | number;
  requested_status?: unknown;
  status?: boolean;
};

type BrandProfileTab = {
  data: BrandProfileItem[];
  icon?: ReactNode;
  key?: string;
  show?: boolean;
  tabName: string;
};

type BrandProfileDetails =
  | BrandProfileTab[]
  | {
      data: null;
    };

type TabContentProps = {
  data: BrandProfileItem[];
  getBrandProfileDetails?: () => void;
  loader?: (_loading: boolean) => void;
  selectedBrand?: unknown;
  tabName?: string;
  user?: {
    logo_name?: string;
  };
};

type BrandProfileTabsProps = {
  selectedBrand?: unknown;
  loader?: (_loading: boolean) => void;
  getBrandProfileDetails?: () => void;
  brandProfileDetails?: BrandProfileDetails;
  user?: {
    logo_name?: string;
  };
};

type ChannelListProps = {
  channels: Array<Record<string, unknown>>;
  getBrandProfileDetails?: () => void;
  loader?: (_loading: boolean) => void;
  showRequestAccess: boolean;
  showToolTip: boolean;
};

type ContractDetailsCardProps = {
  contractDetails: BrandProfileItem;
};

type BrandLogoCardProps = {
  logoUrl?: string;
};

const ChannelList = ChannelListBase as ComponentType<ChannelListProps>;
const ContractDetailsCard =
  ContractDetailsCardBase as ComponentType<ContractDetailsCardProps>;
const BrandLogoCard = BrandLogoCardBase as ComponentType<BrandLogoCardProps>;
const Row = RowBase as ComponentType<Record<string, unknown>>;
const Col = ColBase as ComponentType<Record<string, unknown>>;
const Card = CardBase as ComponentType<Record<string, unknown>>;
const CardBody = CardBase.Body as ComponentType<Record<string, unknown>>;
const Grid = GridBase as ComponentType<
  ComponentProps<typeof GridBase> & {
    item?: boolean;
    size?: {
      xs?: number;
      sm?: number;
      md?: number;
    };
  }
>;

// Custom styled Tabs and Tab to match the design in the image, using theme.palette.primary.main
const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 40,
  paddingLeft: "20px",
  borderBottom: `1px solid ${theme?.palette?.neutral?.[200] || "var(--neutral-200)"}`,
  background: "var(--neutral-white)",
  "& .MuiTabs-indicator": {
    backgroundColor: theme?.palette?.primary?.main,
    height: 3,
    borderRadius: "var(--radius-round)",
    transition: "all 0.3s cubic-bezier(.4,0,.2,1)",
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 40,
  minWidth: 120,
  fontWeight: 600,

  fontSize: "0.9rem",
  color: theme.palette.text.secondary,
  opacity: 1,
  textTransform: "none",
  marginRight: 24,
  display: "flex",
  alignItems: "center",
  transition: "color 0.2s cubic-bezier(.4,0,.2,1)",
  "&:not(:last-child)": {
    marginRight: 32,
  },
  "&:hover": {
    color: theme?.palette?.primary?.main,
  },
  "&.Mui-selected": {
    color: theme?.palette?.primary?.main,
    fontWeight: 600,
  },
  "& .MuiTab-wrapper": {
    flexDirection: "row",
    gap: 8,
  },
  "&.MuiButtonBase-root.MuiTab-root": {
    padding: "6px 14px !important",
  },
}));

// Helper function to format specific date fields
const formatDateField = (item: BrandProfileItem) => {
  const dateFields = [
    "contractStartDate",
    "contractEndDate",
    "dashboardDateAsOfDate",
  ];

  if (dateFields.includes(item.key) && item.value) {
    return getFormatDate(item.value, "MM/DD/YYYY");
  }

  return item.value;
};

const TabContent = ({
  data,
  loader,
  getBrandProfileDetails,
  user = {},
}: TabContentProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const isTablet = useMediaQuery(theme.breakpoints.only("sm"));

  let columns = 5;
  if (isMobile) {
    columns = 2;
  } else if (isTablet) {
    columns = 3;
  }
  const filteredData = data.filter(
    (item) =>
      item.value !== null &&
      item.value !== undefined &&
      item.value !== "" &&
      item.label !== "Attach Brand Logo"
  );
  const rows = chunkArray(filteredData, columns) as BrandProfileItem[][];

  // Check if brand profile data exists

  const { copyOfContract, attachBrandLogo } = data.reduce<{
    attachBrandLogo?: BrandProfileValue;
    copyOfContract?: BrandProfileItem;
  }>((acc, item) => {
    if (item.key === "copyOfContract") acc.copyOfContract = item;
    if (item.key === "attachBrandLogo") acc.attachBrandLogo = item.value;
    return acc;
  }, {});

  return (
    <div className="pr-4 pl-4">
      <div className="table-responsive">
        <table className="table brand-profile-table">
          <tbody>
            {rows.map((row, rowIdx) => (
              <Fragment key={rowIdx}>
                {/* Label Row */}
                <tr className="align-top">
                  {row.map((item, colIdx) => (
                    <td
                      key={colIdx}
                      className="align-top"
                      style={{
                        width: `${100 / columns}%`,
                      }}
                    >
                      <Typography
                        color="text.primary"
                        className="tab-content-label mt-2"
                      >
                        {item.label}
                      </Typography>
                    </td>
                  ))}
                </tr>

                {/* Value Row */}
                <tr className="align-top">
                  {row.map((item, colIdx) => (
                    <td
                      key={colIdx}
                      className="align-top brand-profile-table-cell"
                      style={{
                        width: `${100 / columns}%`,
                      }}
                    >
                      <Typography
                        color="text.primary"
                        className="tab-content-value"
                      >
                        {item.key === "channelsActivated" &&
                        Array.isArray(item.value) ? (
                          <ChannelList
                            channels={(item.value as BrandChannel[]).map(
                              (ch) => ({
                                id: ch.id.toString(),
                                name: ch.channel_name,
                                status: ch.status ? "active" : "inactive",
                                image: ch.id,
                                altText: ch.channel_name,
                                grayscale: !ch.status,
                                requested_status: ch.requested_status,
                              })
                            )}
                            getBrandProfileDetails={getBrandProfileDetails}
                            loader={loader}
                            showRequestAccess={true}
                            showToolTip={true}
                          />
                        ) : item.key === "copyOfContract" ||
                          item.key === "website" ||
                          item.key === "fbPage" ? (
                          <a
                            href={
                              item.key === "copyOfContract"
                                ? getImage(
                                    String(item.value),
                                    "ASSET_DOWNLOAD_URL"
                                  )
                                : String(item.value)
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "var(--primary-base)" }}
                          >
                            {item.key === "copyOfContract"
                              ? item.fileName
                              : String(item?.value)}
                          </a>
                        ) : Array.isArray(item.value) ? (
                          item.value.map((val, idx) => (
                            <div key={idx}>
                              {typeof val === "object"
                                ? JSON.stringify(val)
                                : val}
                            </div>
                          ))
                        ) : item.key === "status" && item.value === 1 ? (
                          "ACTIVE"
                        ) : item.key === "status" && item.value === 0 ? (
                          "INACTIVE"
                        ) : item.value === 0 ? (
                          "NO"
                        ) : item.value === 1 ? (
                          "YES"
                        ) : (
                          (formatDateField(item) as ReactNode)
                        )}
                      </Typography>
                    </td>
                  ))}
                  {row.length < columns &&
                    Array.from({ length: columns - row.length }).map((_, i) => (
                      <td
                        key={`empty-value-${i}`}
                        className="brand-profile-table-cell"
                      />
                    ))}
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Grid container spacing={3}>
        {copyOfContract && copyOfContract.value && (
          <Grid
            item
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <ContractDetailsCard contractDetails={copyOfContract} />
          </Grid>
        )}
        {attachBrandLogo && (
          <Grid
            item
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <BrandLogoCard logoUrl={user?.logo_name} />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

const BrandProfileTabs = ({
  selectedBrand,
  loader,
  getBrandProfileDetails = () => {},
  brandProfileDetails,
  user,
}: BrandProfileTabsProps) => {
  const [selectedTab, setSelectedTab] = useState(0);
  // State for fetched brand profile details
  // useEffect(() => {
  //   getBrandProfileDetails();
  // }, [selectedBrand?.id, loader]);

  // Helper function to check if all values in a tab's data are null/undefined/empty
  const hasValidData = (tabData?: BrandProfileItem[]) => {
    if (!Array.isArray(tabData) || tabData.length === 0) {
      return false;
    }
    return tabData.some(
      (item) =>
        item.value !== null && item.value !== undefined && item.value !== ""
    );
  };

  const visibleTabs =
    brandProfileDetails &&
    Array.isArray(brandProfileDetails) &&
    brandProfileDetails
      ?.map((tab, _idx) => ({
        ...tab,
        key: `tab-${_idx}`,
        show: true,
      }))
      .filter((tab) => tab.show && hasValidData(tab.data));

  const handleTabChange = (_event: SyntheticEvent, newValue: number) =>
    setSelectedTab(newValue);
  const dataNotFound =
    !Array.isArray(brandProfileDetails) && brandProfileDetails?.data === null;
  return (
    <Row>
      {dataNotFound ? (
        <Col xs={12} md={8} lg={12}>
          <div>
            <Alert severity="info">
              <strong>No Brand Profile Data found.</strong>
            </Alert>
          </div>
        </Col>
      ) : (
        <>
          <Col xs={12}>
            <StyledTabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {visibleTabs &&
                visibleTabs?.map((tab) => (
                  <StyledTab
                    key={tab.key}
                    label={
                      <>
                        {tab.icon && (
                          <span className="tab-icon-wrapper">{tab.icon}</span>
                        )}
                        {tab.tabName}
                      </>
                    }
                    disableRipple
                  />
                ))}
            </StyledTabs>
          </Col>
          <Col xs={12} className="mt-3 ">
            <Card className="rounded-0 border-0 tab-card">
              <CardBody className="p-0 pb-5">
                <TabContent
                  data={(visibleTabs && visibleTabs[selectedTab]?.data) || []}
                  loader={loader}
                  selectedBrand={selectedBrand}
                  tabName={visibleTabs?.[selectedTab]?.tabName}
                  getBrandProfileDetails={getBrandProfileDetails}
                  user={user}
                />
              </CardBody>
            </Card>
          </Col>
        </>
      )}
    </Row>
  );
};

export default BrandProfileTabs;
